// Subco (Alt Taşeron) Yönetimi — Şub 28, 2026
// SUPER_ADMIN subco kullanıcılarını buradan CRUD yapar. Standart Kullanıcı
// Yönetimi'nde görünmezler. Planning ve Routes/Turlar sayfalarında şoför
// dropdown'larında firma filtresine göre listelenirler.
import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, RefreshCw, Loader2, Pencil, Trash2, KeyRound, Users, Truck, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../lib/auth';

export default function SubcoManagement() {
  const { user } = useAuthStore();
  const [firms, setFirms] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterFirmId, setFilterFirmId] = useState('__all__');
  const [showInactive, setShowInactive] = useState(false);

  const [editing, setEditing] = useState(null); // null | { ...subco } | '__new__'
  const [pwTarget, setPwTarget] = useState(null); // subco id for password change
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    api.get('/admin/firms').then((r) => setFirms(r.data?.data || []));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterFirmId !== '__all__') params.set('firm_id', filterFirmId);
      if (showInactive) params.set('include_inactive', 'true');
      const r = await api.get(`/subcos?${params.toString()}`);
      setItems(r.data?.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Subco listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [filterFirmId, showInactive]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/subcos/${deleteTarget.id}`);
      toast.success(`${deleteTarget.full_name} pasif edildi`);
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silinemedi');
    }
  };

  return (
    <div className="space-y-4 p-4 max-w-7xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            Subco (Alt Taşeron) Yönetimi
          </h1>
          <p className="text-sm text-gray-500">
            Birden fazla firma altında çalışabilen, sisteme email+şifre ile giriş yapabilen
            alt taşeron kullanıcıları. Yalnızca Planlama ve Routes/Turlar sayfalarında görünür.
          </p>
        </div>
        {isSuperAdmin && (
          <Button onClick={() => setEditing('__new__')} data-testid="subco-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Yeni Subco Ekle
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filterFirmId} onValueChange={setFilterFirmId}>
              <SelectTrigger className="w-[220px]" data-testid="subco-firm-filter">
                <SelectValue placeholder="Firma filtresi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tüm Firmalar</SelectItem>
                {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={showInactive} onCheckedChange={setShowInactive} data-testid="subco-inactive-toggle" />
              Pasifleri Göster
            </label>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <span className="text-xs text-gray-500 ml-auto">{items.length} subco</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-x-auto">
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>İşletme Adı</TableHead>
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Plaka</TableHead>
                  <TableHead>Firmalar</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-gray-500">
                    Henüz subco yok. "Yeni Subco Ekle" ile başlayın.
                  </TableCell></TableRow>
                )}
                {items.map((s) => (
                  <TableRow key={s.id} data-testid={`subco-row-${s.id}`}>
                    <TableCell className="font-semibold text-indigo-700">
                      {s.company_name || <span className="text-gray-300 text-xs font-normal">—</span>}
                    </TableCell>
                    <TableCell className="font-medium">{s.full_name}</TableCell>
                    <TableCell className="text-xs"><Mail className="h-3 w-3 inline mr-1 text-gray-400" />{s.email}</TableCell>
                    <TableCell>
                      {s.vehicle_code ? (
                        <Badge variant="outline" className="font-mono"><Truck className="h-3 w-3 mr-1" />{s.vehicle_code}</Badge>
                      ) : <span className="text-gray-400 text-xs">-</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {(s.firms || []).length === 0 ? (
                        <span className="text-gray-400">Firma atanmamış</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {s.firms.map((f) => <Badge key={f.id} variant="secondary" className="text-[10px]">{f.name}</Badge>)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={s.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}>
                        {s.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(s)} className="h-8 w-8" data-testid={`subco-edit-${s.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {isSuperAdmin && (
                          <>
                            <Button size="icon" variant="ghost" onClick={() => setPwTarget(s)} className="h-8 w-8" title="Şifre Değiştir">
                              <KeyRound className="h-3.5 w-3.5 text-amber-600" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(s)} className="h-8 w-8" title="Pasif Et">
                              <Trash2 className="h-3.5 w-3.5 text-red-600" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <SubcoEditDialog
        open={!!editing}
        subco={editing === '__new__' ? null : editing}
        firms={firms}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchData(); }}
      />

      <SubcoPasswordDialog
        open={!!pwTarget}
        subco={pwTarget}
        onClose={() => setPwTarget(null)}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Subco'yu pasif etmek istediğine emin misin?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.full_name} pasif edilecek ve Planning dropdown'larında görünmeyecek. Geri alabilirsin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Pasif Et</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SubcoEditDialog({ open, subco, firms, onClose, onSaved }) {
  const isNew = !subco;
  const [companies, setCompanies] = useState([]);
  useEffect(() => { if (open) api.get('/subco-companies').then((r) => setCompanies(r.data?.data || [])).catch(() => {}); }, [open]);
  const [form, setForm] = useState({
    full_name: '', company_name: '', subco_id: '', email: '', password: '', phone: '', vehicle_code: '',
    firm_ids: [], note: '', status: 'ACTIVE',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (subco) {
        setForm({
          full_name: subco.full_name || '',
          company_name: subco.company_name || '',
          subco_id: subco.subco_id || '',
          email: subco.email || '',
          password: '',
          phone: subco.phone || '',
          vehicle_code: subco.vehicle_code || '',
          firm_ids: subco.firm_ids || [],
          note: subco.note || '',
          status: subco.status || 'ACTIVE',
        });
      } else {
        setForm({ full_name: '', company_name: '', subco_id: '', email: '', password: '', phone: '', vehicle_code: '', firm_ids: [], note: '', status: 'ACTIVE' });
      }
    }
  }, [open, subco]);

  const toggleFirm = (fid) => {
    setForm((f) => ({
      ...f,
      firm_ids: f.firm_ids.includes(fid) ? f.firm_ids.filter((x) => x !== fid) : [...f.firm_ids, fid],
    }));
  };

  const save = async () => {
    if (!form.full_name.trim()) { toast.error('Ad soyad zorunlu'); return; }
    if (isNew && (!form.email.trim() || !form.password.trim())) {
      toast.error('Email ve şifre zorunlu');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/subcos', {
          full_name: form.full_name, company_name: form.company_name || null, subco_id: form.subco_id || null,
          email: form.email, password: form.password,
          phone: form.phone || null, vehicle_code: form.vehicle_code || null,
          firm_ids: form.firm_ids, note: form.note || null,
        });
        toast.success('Subco oluşturuldu');
      } else {
        await api.put(`/subcos/${subco.id}`, {
          full_name: form.full_name, company_name: form.company_name || null, subco_id: form.subco_id || '',
          phone: form.phone || null,
          vehicle_code: form.vehicle_code || null, firm_ids: form.firm_ids,
          note: form.note || null, status: form.status,
        });
        toast.success('Subco güncellendi');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] flex flex-col p-0" data-testid="subco-edit-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b flex-shrink-0">
          <DialogTitle>{isNew ? 'Yeni Subco Ekle' : 'Subco Düzenle'}</DialogTitle>
          <DialogDescription>
            {isNew ? 'Alt taşeron için email+şifre ile giriş yapabilen kullanıcı oluştur.' : subco?.email}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 px-6 py-4 overflow-y-auto flex-1">
          <div>
            <Label>Subco firması</Label>
            <select
              value={form.subco_id}
              onChange={(e) => {
                const c = companies.find((x) => x.id === e.target.value);
                setForm({ ...form, subco_id: e.target.value, company_name: c ? c.name : form.company_name, firm_ids: c && !form.firm_ids.length ? (c.firm_ids || []) : form.firm_ids });
              }}
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              data-testid="subco-company-select"
            >
              <option value="">— Seçin (Firmalar → Subco Listesi) —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <p className="text-[10px] text-gray-400 mt-1">
              Route/Planlamada "<b>{form.company_name || '—'}</b> — {form.full_name || 'Ad Soyad'}" olarak gösterilir.
            </p>
          </div>
          <div>
            <Label>Ad Soyad *</Label>
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="subco-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Email {isNew && '*'}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!isNew} data-testid="subco-email" />
              {!isNew && <p className="text-[10px] text-gray-400 mt-1">Değiştirmek için Kullanıcı Yönetimi'ni kullan (SUPER_ADMIN).</p>}
            </div>
            <div>
              <Label>Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          {isNew && (
            <div>
              <Label>Şifre *</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={6} data-testid="subco-password" />
            </div>
          )}
          <div>
            <Label>Sanal Araç Plakası</Label>
            <Input value={form.vehicle_code} onChange={(e) => setForm({ ...form, vehicle_code: e.target.value.toUpperCase() })} placeholder="1ABC123" className="font-mono" data-testid="subco-vehicle" />
          </div>
          <div>
            <Label>Firmalar (çok seçimli) {form.firm_ids.length > 0 && <span className="text-xs text-indigo-600 font-normal">— {form.firm_ids.length} firma seçili</span>}</Label>
            <div className="border rounded p-2 max-h-40 overflow-auto space-y-1">
              {firms.length === 0 && <p className="text-xs text-gray-400">Firma yok</p>}
              {firms.map((f) => (
                <label key={f.id} className="flex items-center gap-2 text-sm hover:bg-indigo-50 px-1 py-0.5 rounded cursor-pointer">
                  <Checkbox checked={form.firm_ids.includes(f.id)} onCheckedChange={() => toggleFirm(f.id)} />
                  <span className="font-medium">{f.name}</span>
                  {f.city && <span className="text-xs text-gray-400">· {f.city}</span>}
                </label>
              ))}
            </div>
            {form.firm_ids.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.firm_ids.map((fid) => {
                  const f = firms.find((x) => x.id === fid);
                  return f ? <Badge key={fid} variant="secondary" className="text-[10px]">{f.name}</Badge> : null;
                })}
              </div>
            )}
          </div>
          <div>
            <Label>Not</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          {!isNew && (
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Aktif</SelectItem>
                  <SelectItem value="SUSPENDED">Pasif</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-3 border-t flex-shrink-0 bg-white">
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="subco-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubcoPasswordDialog({ open, subco, onClose }) {
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setPw(''); }, [open]);
  const save = async () => {
    if (!pw || pw.length < 6) { toast.error('Şifre en az 6 karakter'); return; }
    setSaving(true);
    try {
      await api.put(`/subcos/${subco.id}/password`, { password: pw });
      toast.success('Şifre güncellendi');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Güncellenemedi');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Şifre Değiştir</DialogTitle>
          <DialogDescription>{subco?.full_name} ({subco?.email})</DialogDescription>
        </DialogHeader>
        <div>
          <Label>Yeni Şifre</Label>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} minLength={6} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
